import React from 'react';
import { useSelector } from 'react-redux';
import styles from './index.module.scss';
import CollectionCard from './collection_card';

/**
 * Линия коллекций. Рендерится в tab_list при template === 'collection_line'.
 * Блок 0: publication.content[0]. block_content[0] — данные всей линии (content_text = заголовок линии, links_names).
 * Остальные элементы block_content (с индекса 1) — карточки коллекций.
 */
const CollectionLine = ({ publication, globalIndex }) => {
  const containerRef = React.useRef(null);
  const tabs = useSelector((state) => state.tabs_data?.tabs ?? []);

  const block0 = React.useMemo(() => {
    if (!publication?.content) return null;
    try {
      const parsed = JSON.parse(publication.content);
      return Array.isArray(parsed) && parsed[0] != null ? parsed[0] : null;
    } catch {
      return null;
    }
  }, [publication?.content]);

  const lineData = React.useMemo(() => {
    const bc = block0?.block_content;
    return Array.isArray(bc) && bc[0] != null ? bc[0] : null;
  }, [block0]);

  const lineTitle = lineData?.content_text ?? lineData?.cotent_text ?? publication?.show_name ?? 'Коллекции';
  const lineLinksNames = lineData?.links_names ?? null;

  const { collection_tab_name, collection_links } = React.useMemo(() => {
    const raw = lineLinksNames != null ? String(lineLinksNames).trim() : '';
    const byPipe = raw ? raw.split('|').map((s) => s.trim()) : [];
    const collection_tab_name = byPipe[0] ?? '';
    const secondPart = byPipe[1] ?? '';
    const collection_links = secondPart ? secondPart.split('*').map((s) => s.trim()).filter(Boolean) : [];
    return { collection_tab_name, collection_links };
  }, [lineLinksNames]);

  const collections = React.useMemo(() => {
    const bc = block0?.block_content;
    if (!Array.isArray(bc) || bc.length <= 1) return [];
    return bc.slice(1);
  }, [block0]);

  const path_to_folder = React.useMemo(() => {
    if (!publication?.path || !publication?.eng_name) return null;
    return `https://${publication.path}${publication.eng_name}/`;
  }, [publication?.path, publication?.eng_name]);

  const tabByCollectionName = React.useMemo(() => {
    if (!collection_tab_name) return null;
    return tabs.find((tab) => (tab.eng_name || tab.blank_name || '').toLowerCase() === collection_tab_name.toLowerCase()) ?? null;
  }, [tabs, collection_tab_name]);

  const sectionExistsByTabName = !!tabByCollectionName;

  const collectionCardsFromPublications = React.useMemo(() => {
    if (!sectionExistsByTabName || !Array.isArray(collection_links)) return [];
    const tabPublications = tabByCollectionName?.publications ?? [];
    return collection_links
      .map((link) =>
        tabPublications.find(
          (pub) => (pub.eng_name || pub.blank_name || '').toLowerCase() === String(link).toLowerCase()
        )
      )
      .filter(Boolean);
  }, [sectionExistsByTabName, tabByCollectionName, collection_links]);

  React.useEffect(() => {
    console.log('[CollectionLine] данные линии коллекции:', {
      publication,
      globalIndex,
      collections,
      path_to_folder,
      collectionsCount: collections.length,
    });
  }, [publication, globalIndex, collections, path_to_folder]);

  React.useEffect(() => {
    console.log('[CollectionLine] проверка раздела по collection_tab_name:', {
      collection_tab_name,
      sectionExists: sectionExistsByTabName,
      tabsCount: tabs.length,
    });
  }, [collection_tab_name, sectionExistsByTabName, tabs.length]);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const isDownRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const scrollStartRef = React.useRef(0);
  const [hasDragged, setHasDragged] = React.useState(false);

  const scrollLeft = (e) => {
    e.stopPropagation();
    if (containerRef.current) containerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
  };
  const scrollRight = (e) => {
    e.stopPropagation();
    if (containerRef.current) containerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
  };

  const onMouseDown = (e) => {
    if (!containerRef.current) return;
    isDownRef.current = true;
    setHasDragged(false);
    startXRef.current = e.clientX;
    scrollStartRef.current = containerRef.current.scrollLeft;
    containerRef.current.classList.add(styles.dragging);
  };
  const onMouseMove = (e) => {
    if (!containerRef.current || !isDownRef.current) return;
    const dx = e.clientX - startXRef.current;
    if (Math.abs(dx) > 5) setHasDragged(true);
    containerRef.current.scrollLeft = scrollStartRef.current - dx;
  };
  const endDrag = () => {
    isDownRef.current = false;
    if (containerRef.current) containerRef.current.classList.remove(styles.dragging);
    setTimeout(() => setHasDragged(false), 100);
  };

  return (
    <div className={styles.collectionLine}>
      <h2 className={styles.lineTitle}>{lineTitle}</h2>
      <div className={styles.lineWrapper}>
        <button type="button" className={styles.scrollButton} onClick={scrollLeft} aria-label="Прокрутить влево">
          ‹
        </button>
        <div
          className={styles.lineContainer}
          ref={containerRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}>
          {sectionExistsByTabName && collectionCardsFromPublications.length > 0 ? (
            collectionCardsFromPublications.map((pub, index) => {
              const cardPath = pub.path && (pub.eng_name || pub.blank_name)
                ? `https://${pub.path}${pub.eng_name || pub.blank_name}/`
                : path_to_folder;
              return (
                <CollectionCard
                  key={pub.eng_name || pub.id || pub.blank_name || index}
                  item={pub}
                  path_to_folder={cardPath}
                  hasDragged={hasDragged}
                  parentTab={tabByCollectionName?.eng_name ?? tabByCollectionName?.blank_name ?? collection_tab_name}
                />
              );
            })
          ) : collections.length === 0 ? (
            <div className={styles.emptyMessage}>Коллекции не добавлены</div>
          ) : (
            collections.map((item, index) => (
              <CollectionCard
                key={item.eng_name || item.id || index}
                item={item}
                path_to_folder={path_to_folder}
                hasDragged={hasDragged}
              />
            ))
          )}
        </div>
        <button type="button" className={styles.scrollButton} onClick={scrollRight} aria-label="Прокрутить вправо">
          ›
        </button>
      </div>
    </div>
  );
};

export default CollectionLine;
